import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { CreditCard, Truck, FileText } from 'lucide-react';
import { wholesaleService } from '../../services/wholesaleService';
import { useAuth } from '../../contexts/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const CheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderSummary, setOrderSummary] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [useNewCard, setUseNewCard] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);
  const [orderSummaryLoading, setOrderSummaryLoading] = useState(true);
  // Update fetchOrderSummary to accept cart as a parameter

  const fetchOrderSummary = async () => {
    try {
      if (!location.state?.cart) {
        throw new Error('No cart data available');
      }
      const response = await wholesaleService.calculateOrder(location.state.cart);
      setOrderSummary(response);
    } catch (error) {
      setError('Error calculating order total');
      console.error('Error details:', error);
    } finally {
      setOrderSummaryLoading(false);
    }
  };
  
  useEffect(() => {
    // Ensure user is authenticated and cart exists
    if (!user) {
      navigate('/login');
      return;
    }
  
    if (!location.state?.cart) {
      navigate('/wholesale/catalog');
      return;
    }
  
    fetchOrderSummary();
    fetchPaymentMethods();
  }, [user, location.state, navigate]);

  const fetchPaymentMethods = async () => {
    try {
      const paymentMethods = await wholesaleService.getPaymentMethods();
      setSavedPaymentMethods(paymentMethods);
      if (paymentMethods.length > 0) {
        setSelectedPaymentMethod(paymentMethods[0].id);
      } else {
        setUseNewCard(true);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setError('Unable to load saved payment methods. You can proceed with a new card.');
      setUseNewCard(true);
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!termsAccepted) {
      setError('Please accept the terms and conditions');
      return;
    }
  
    setLoading(true);
    setError('');
  
    try {
      let paymentMethodId;
  
      if (useNewCard) {
        // Create new payment method with Stripe
        const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement(CardElement),
        });
  
        if (stripeError) throw new Error(stripeError.message);
        paymentMethodId = paymentMethod.id;
      } else {
        paymentMethodId = selectedPaymentMethod;
      }
  
      console.log('Submitting order with:', {
        cart: location.state.cart,
        paymentMethodId,
        useNewCard
      });
  
      // Create order with payment intent
      const response = await wholesaleService.createOrder({
        cart: location.state.cart,
        paymentMethodId,
        useNewCard,
      });
  
      console.log('Order creation response:', response); // Debug log
  
      if (!response.data || !response.data.success) {
        throw new Error(response.data?.message || 'Failed to create order');
      }
  
      // Handle successful order
      navigate('/wholesale/order-confirmation', {
        state: { orderId: response.data.orderId }
      });
    } catch (error) {
      console.error('Order submission error:', error);
      setError(error.message || 'An error occurred during checkout');
    } finally {
      setLoading(false);
    }
  };
  
  if (orderSummaryLoading || paymentMethodsLoading) {
    return (
      <div className="container mt-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }
  return (
    <div className="container my-5">
      <div className="row">
        {/* Order Summary */}
        <div className="col-md-4 order-md-2 mb-4">
          <div className="card">
            <div className="card-body">
              <h4 className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-primary">Order Summary</span>
                <span className="badge bg-primary rounded-pill">
                  {Object.values(location.state.cart).reduce((a, b) => a + b, 0)}
                </span>
              </h4>
              <ul className="list-group mb-3">
                {orderSummary.items.map(item => (
                  <li key={item.productID} className="list-group-item d-flex justify-content-between lh-sm">
                    <div>
                      <h6 className="my-0">{item.name}</h6>
                      <small className="text-muted">
                        {item.quantity} cases × ${item.price}
                      </small>
                    </div>
                    <span className="text-muted">${(item.quantity * item.price).toFixed(2)}</span>
                  </li>
                ))}
                <li className="list-group-item d-flex justify-content-between">
                  <span>Subtotal</span>
                  <strong>${orderSummary.subtotal.toFixed(2)}</strong>
                </li>
                <li className="list-group-item d-flex justify-content-between bg-light">
                  <div className="text-success">
                    <h6 className="my-0">Payment Terms</h6>
                    <small>Net 30 Days</small>
                  </div>
                  <span className="text-success">Due: {orderSummary.dueDate}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Checkout Form */}
        <div className="col-md-8 order-md-1">
          <h4 className="mb-3">Checkout Details</h4>
          <form onSubmit={handleSubmit}>
            {/* Payment Method Selection */}
            <div className="mb-4">
              <h5 className="mb-3">
                <CreditCard className="me-2" size={20} />
                Payment Method
              </h5>
              {savedPaymentMethods.length > 0 && (
                <div className="mb-3">
                  {savedPaymentMethods.map(method => (
                    <div key={method.id} className="form-check mb-2">
                      <input
                        type="radio"
                        className="form-check-input"
                        name="paymentMethod"
                        id={method.id}
                        checked={selectedPaymentMethod === method.id && !useNewCard}
                        onChange={() => {
                          setSelectedPaymentMethod(method.id);
                          setUseNewCard(false);
                        }}
                      />
                      <label className="form-check-label" htmlFor={method.id}>
                        {method.brand} ending in {method.last4} (expires {method.exp_month}/{method.exp_year})
                      </label>
                    </div>
                  ))}
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="paymentMethod"
                      id="newCard"
                      checked={useNewCard}
                      onChange={() => setUseNewCard(true)}
                    />
                    <label className="form-check-label" htmlFor="newCard">
                      Use a new card
                    </label>
                  </div>
                </div>
              )}

              {useNewCard && (
                <div className="card">
                  <div className="card-body">
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#424770',
                            '::placeholder': {
                              color: '#aab7c4',
                            },
                          },
                          invalid: {
                            color: '#9e2146',
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Shipping Information */}
            <div className="mb-4">
              <h5 className="mb-3">
                <Truck className="me-2" size={20} />
                Shipping Address
              </h5>
              <div className="card">
                <div className="card-body">
                  <p className="mb-0">
                    {orderSummary.shippingAddress.line1}<br />
                    {orderSummary.shippingAddress.line2 && <>{orderSummary.shippingAddress.line2}<br /></>}
                    {orderSummary.shippingAddress.city}, {orderSummary.shippingAddress.state} {orderSummary.shippingAddress.zip}
                  </p>
                </div>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="mb-4">
              <h5 className="mb-3">
                <FileText className="me-2" size={20} />
                Terms and Conditions
              </h5>
              <div className="card mb-3">
                <div className="card-body">
                  <h6>Payment Terms (CA B&P Code §25509)</h6>
                  <ul className="mb-0">
                    <li>Payment will be processed 30 days from delivery date</li>
                    <li>Your card will be pre-authorized at the time of order</li>
                    <li>The full payment amount will be charged automatically after 30 days</li>
                    <li>Pricing and availability are subject to change</li>
                  </ul>
                </div>
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="acceptTerms"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  required
                />
                <label className="form-check-label" htmlFor="acceptTerms">
                  I accept the terms and conditions of the sale
                </label>
              </div>
            </div>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <button
              className="btn btn-primary btn-lg w-100"
              type="submit"
              disabled={loading || !stripe || !termsAccepted}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Processing...
                </>
              ) : (
                'Place Order'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const WholesaleCheckout = () => (
  <Elements stripe={stripePromise}>
    <CheckoutForm />
  </Elements>
);

export default WholesaleCheckout;